import { useEffect, useMemo, useState } from "react";
import { Play, Pause, Square, Volume2, ChevronDown, AudioLines } from "lucide-react";

/**
 * LessonAudioBar — UI controls for narration. Sticky-bottom on small screens,
 * inline-card on desktop. Wires play/pause/stop + voice picker + rate slider.
 *
 * Props:
 *   - narration: object returned from useLessonNarration()
 *   - sentences: flat array of {id, text} the parent has built from lesson sections
 */
export function LessonAudioBar({ narration, sentences }) {
  const { state, supported, play, pause, resume, stop, voices, voice, setVoice, rate, setRate } = narration;
  const [open, setOpen] = useState(false);

  // Reset open menu when narration stops
  useEffect(() => { if (state === "idle") setOpen(false); }, [state]);

  const totalChars = useMemo(() => sentences.reduce((sum, s) => sum + s.text.length, 0), [sentences]);
  const estMinutes = Math.max(1, Math.round(totalChars / (rate * 1000)));   // very rough: ~1000 chars/min at 1×

  if (!supported) {
    return (
      <div className="bg-amber-50 border-l-4 border-amber-500 p-3 text-xs text-zinc-700 font-mono" data-testid="narration-unsupported">
        Your browser doesn't support audio narration. Try Chrome, Edge, or Safari.
      </div>
    );
  }

  const handlePrimary = () => {
    if (state === "idle") play(sentences);
    else if (state === "playing") pause();
    else if (state === "paused") resume();
  };

  return (
    <div className="bg-zinc-950 text-white border border-zinc-800" data-testid="lesson-audio-bar">
      <div className="flex items-center gap-3 p-3 sm:p-4">
        {/* Play / pause */}
        <button
          onClick={handlePrimary}
          aria-label={state === "playing" ? "Pause narration" : state === "paused" ? "Resume narration" : "Play narration"}
          className="bg-red-600 hover:bg-red-500 text-white w-11 h-11 grid place-items-center transition-colors flex-shrink-0"
          data-testid="narration-play-btn"
        >
          {state === "playing" ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
        </button>

        {/* Stop */}
        <button
          onClick={stop}
          disabled={state === "idle"}
          aria-label="Stop narration"
          className="text-zinc-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          data-testid="narration-stop-btn"
        >
          <Square className="w-4 h-4" fill="currentColor" />
        </button>

        {/* Status text */}
        <div className="flex-1 min-w-0">
          <div className="text-xs font-mono uppercase tracking-widest text-zinc-400 flex items-center gap-2" data-testid="narration-status">
            <AudioLines className={`w-3.5 h-3.5 ${state === "playing" ? "text-red-500" : "text-zinc-500"}`} />
            {state === "idle" && (<>Listen to this lesson · ~{estMinutes} min</>)}
            {state === "playing" && "Playing — tap pause to study a section"}
            {state === "paused" && "Paused"}
          </div>
        </div>

        {/* Settings toggle */}
        <button
          onClick={() => setOpen((v) => !v)}
          aria-label="Voice and speed settings"
          className="text-zinc-400 hover:text-white transition-colors inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest"
          data-testid="narration-settings-toggle"
        >
          <Volume2 className="w-4 h-4" />
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>

      {/* Settings panel */}
      {open && (
        <div className="border-t border-zinc-800 p-3 sm:p-4 space-y-3" data-testid="narration-settings-panel">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 block mb-1.5">
              Voice
            </label>
            <select
              value={voice?.voiceURI || ""}
              onChange={(e) => {
                const v = voices.find((vv) => vv.voiceURI === e.target.value);
                if (v) setVoice(v);
              }}
              className="w-full bg-zinc-900 border border-zinc-700 text-white text-xs font-mono px-3 py-2 focus:border-red-600 outline-none"
              data-testid="narration-voice-select"
            >
              {voices.map((v) => (
                <option key={v.voiceURI} value={v.voiceURI}>
                  {v.name} ({v.lang})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 flex justify-between items-baseline mb-1.5">
              <span>Speed</span>
              <span className="text-white font-bold">{rate.toFixed(2)}×</span>
            </label>
            <input
              type="range"
              min="0.6"
              max="1.8"
              step="0.05"
              value={rate}
              onChange={(e) => setRate(parseFloat(e.target.value))}
              className="w-full accent-red-600"
              data-testid="narration-rate-slider"
            />
            <div className="flex justify-between text-[10px] font-mono text-zinc-500 mt-1">
              <span>0.6×</span>
              <span>1×</span>
              <span>1.8×</span>
            </div>
          </div>

          <p className="text-[10px] font-mono text-zinc-500 leading-relaxed pt-1">
            Voices are provided by your browser. Quality varies — try a different voice if you don't like the default.
          </p>
        </div>
      )}
    </div>
  );
}
